/**
 * Resolves which festival this page is for, once, at the top of the tree.
 *
 * The bare platform domain has no tenant — that is the discovery directory.
 * A festival subdomain has one, and everything below can rely on it.
 */
import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, endpoints, type TenantEdition } from '@/lib/api';
import { loadTenant, type TenantAnswer } from '@/offline/tenantCache';
import { TimeoutError } from '@/offline/net';
import { editionSlugFromPath, resolveTenant } from '@/lib/tenant';
import { ErrorState, LoadingScreen } from '@/ui';
import { useT } from '@/i18n';
import { ToApp } from './ToApp';
import { OfflineReentry } from './OfflineReentry';
import { reentryCandidate } from '@/lib/offlineReentry';

export interface Tenant {
  festivalSlug: string;
  festivalName: string;
  editionId: string | null;
  editionSlug: string | null;
  timezone: string | null;
  /** Every published edition of the festival — what `/<slug>` may name. */
  editions: TenantEdition[];
  /**
   * The edition the bare subdomain opens. Differs from `editionId` only when
   * the address names another year (`ozora.myrave.io/2026`).
   */
  defaultEditionId: string | null;
  /** The festival's cover, from `/tenant` — the backdrop while the lineup loads. */
  coverUrl: string | null;
  /**
   * What the loading screen shows of the rave before its bundle arrives:
   * the chosen edition's logo (else the festival's), its place and country.
   */
  logoUrl: string | null;
  venue: string | null;
  country: string | null;
  /**
   * Not a rave the public may open — nothing published, or private (`/tenant`'s `hidden`, the user, 2026-11-02):
   * the address goes to the main app unless the API lets this person read it (`HiddenRave` in `App.tsx`).
   */
  hidden: boolean;
}

type ApiTenant = NonNullable<Awaited<ReturnType<typeof endpoints.tenant>>['tenant']>;

/**
 * The tenant, with the edition the ADDRESS asks for.
 *
 * The API answers with the festival's default edition (tenancy's rule) and the
 * list of published ones. A first path segment that is one of those slugs
 * picks that year instead; anything else keeps the default. Exported for the
 * test, which is the only other caller.
 */
export function withPathEdition(t: ApiTenant, pathname = window.location.pathname): Tenant {
  const editions = t.editions ?? [];
  const slug = editionSlugFromPath(editions.map((e) => e.slug), pathname);
  const chosen = slug ? editions.find((e) => e.slug === slug) : undefined;
  const shown = chosen ?? editions.find((e) => e.id === t.editionId);
  return {
    festivalSlug: t.festivalSlug,
    festivalName: t.festivalName,
    editionId: chosen?.id ?? t.editionId,
    editionSlug: chosen?.slug ?? t.editionSlug,
    timezone: chosen?.timezone ?? t.timezone,
    editions,
    defaultEditionId: t.editionId,
    coverUrl: t.coverUrl ?? null,
    logoUrl: shown?.logoUrl ?? t.logoUrl ?? null,
    venue: shown?.venue ?? null,
    country: t.country ?? null,
    hidden: t.hidden === true,
  };
}

/**
 * Start the festival's cover downloading as soon as the tenant is known.
 *
 * It is the rave's backdrop and its largest paint, and its address used to
 * arrive only inside the bundle — so the image started after the bundle had
 * landed and rendered, and Lighthouse put the largest paint at 5 s (step 14,
 * 2026-10-05). `/tenant` names it now, and it loads beside the bundle. The URL
 * is the bundle's own, so the backdrop then comes from the browser's cache.
 */
export function preloadImage(url: string | null | undefined): void {
  if (!url || typeof document === 'undefined') return;
  const already = [...document.head.querySelectorAll<HTMLLinkElement>('link[rel="preload"][as="image"]')]
    .some((l) => l.getAttribute('href') === url);
  if (already) return;
  const link = document.createElement('link');
  link.setAttribute('rel', 'preload');
  link.setAttribute('as', 'image');
  link.setAttribute('href', url);
  document.head.appendChild(link);
}

interface TenantState {
  tenant: Tenant | null;
  scope: 'platform' | 'festival';
  /** True while the host says "festival" but the API has not answered yet. */
  loading: boolean;
  /**
   * What this deployment asks of a visitor, before they type anything.
   *
   * Pre-launch, registering does not sign you in — an admin approves first.
   * The sign-up form needs to say so up front: "create an account" and "ask
   * for an account" are different promises, and learning which one it was
   * after submitting reads as a failure rather than a queue.
   *
   * Defaults to false so an older API, or none at all, behaves as the open
   * platform it will eventually be.
   */
  requiresApproval: boolean;
  /**
   * The API SAID approval is switched off — not merely "nothing heard yet".
   *
   * `requiresApproval` defaults to false so screens built for the open
   * platform show nothing about a queue until told otherwise. Letting a held
   * account THROUGH is a different decision: an offline platform host, or the
   * moment before `/tenant` answers, must not read as "the door is open".
   * `RequireApproval` passes on this, never on the default.
   */
  approvalOff: boolean;
}

const Ctx = createContext<TenantState>({ tenant: null, scope: 'platform', loading: false, requiresApproval: false, approvalOff: false });

export function useTenant(): TenantState {
  return useContext(Ctx);
}

/** The edition this page is scoped to. Throws if used outside a festival. */
export function useEditionId(): string {
  const { tenant } = useTenant();
  if (!tenant?.editionId) {
    throw new Error('useEditionId called outside a festival — check the route');
  }
  return tenant.editionId;
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const tr = useT();
  const hint = resolveTenant();

  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['tenant', hint.subdomain],
    // The last good answer for this subdomain answers AT ONCE and the network
    // revalidates it behind — with no signal the rave still boots, and `/2026`
    // still resolves from the stored `editions` (offline/tenantCache.ts). A
    // later 404 drops it and asks again, which lands on the redirect below.
    queryFn: () => loadTenant(hint.subdomain, {
      onLate: (late) => { queryClient.setQueryData<TenantAnswer>(['tenant', hint.subdomain], late); },
      onGone: () => { void queryClient.invalidateQueries({ queryKey: ['tenant', hint.subdomain] }); },
    }),
    // The tenant does not change between two requests from the same page.
    staleTime: 5 * 60_000,
    // Retry a flaky network, never a definitive answer: a 404 means this
    // subdomain has no festival, and asking again cannot change that. Never
    // a TIMEOUT either: that attempt already waited the whole first-visit
    // ceiling, and a second one made hanging wifi a 40-second blank page.
    retry: (count, err) => !(err instanceof ApiError && err.status === 404)
      && !(err instanceof TimeoutError) && count < 1,
  });

  // A subdomain with no festival behind it sends the visitor to the main app
  // (`ToApp`) — a mistyped address should land somewhere useful, not on a dead
  // end, and not on the bare domain, which is the holding page in production.
  //
  // ONLY on a definitive 404. Any other failure — no signal, a 500, a timeout —
  // must leave the visitor where they are: this app is used in a field with no
  // reception, and redirecting a returning visitor away from their own festival
  // because the network is down would discard the entire offline experience.
  const missing = hint.subdomain !== null && error instanceof ApiError && error.status === 404;

  const coverUrl = data?.tenant?.coverUrl;
  useEffect(() => { preloadImage(coverUrl); }, [coverUrl]);

  if (hint.subdomain && isLoading) return <LoadingScreen label={tr('Finding the rave…')} />;
  if (missing) return <ToApp />;

  // Reachable only when the API could not be reached AND no answer for this
  // subdomain was ever stored — a first visit with no signal. Falling through would render the directory on a festival subdomain, which
  // silently discards the tenant rather than reporting that nothing is known
  // yet — and offline is the normal case here, not the exception.
  if (hint.subdomain && error) {
    return (
      <ErrorState
        title={tr('Can’t reach MyRave')}
        detail={tr('{rave} is still there — this device just cannot get online. Anything already downloaded keeps working.', { rave: hint.subdomain })}
        retry={() => window.location.reload()}
        // Signed out here with no signal: the same account may take its offline rave back (w-offline-per-account).
        tip={reentryCandidate() ? <OfflineReentry /> : undefined}
      />
    );
  }

  const value: TenantState = {
    tenant: data?.tenant ? withPathEdition(data.tenant) : null,
    scope: data?.scope ?? 'platform',
    loading: false,
    requiresApproval: data?.auth?.requiresApproval ?? false,
    approvalOff: data?.auth?.requiresApproval === false,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * The tenant with one more edition: a DRAFT its admin opened by address
 * (`DraftRave` in `App.tsx`). `/tenant` lists published editions only, so the
 * draft is added here, for this page alone, once the API has said this person
 * may see it — every screen under it (`useEditionId`, the loading screen, the
 * paywall) then reads the draft as the edition the address names.
 */
export function TenantWithEdition({ edition, children }: { edition: TenantEdition; children: ReactNode }) {
  const state = useContext(Ctx);
  if (!state.tenant) return <>{children}</>;
  const t = state.tenant;
  const value: TenantState = {
    ...state,
    tenant: {
      ...t,
      editionId: edition.id,
      editionSlug: edition.slug,
      timezone: edition.timezone,
      editions: t.editions.some((e) => e.id === edition.id) ? t.editions : [...t.editions, edition],
      logoUrl: edition.logoUrl ?? t.logoUrl,
      venue: edition.venue ?? null,
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
